import {
  RolePermissionsStrict,
  UserCompanyRoleTypes,
  UserCompanyStatusTypes,
  UserRoleTypes,
} from '@/lib/types/UserT'
import { ConfigMap, MapperType } from '@/lib/types/utilsT'

export type UserRoleType = MapperType<UserRoleTypes>
export const ROLES = {
  SUPERADMIN: 'superadmin',
  ADMIN: 'admin',
  USER: 'user',
} satisfies UserRoleType

export type UserCompanyRoleType = MapperType<UserCompanyRoleTypes>
export const USER_COMPANY_ROLES = {
  ADMIN: 'admin',
  OWNER: 'owner',
  MANAGER: 'manager',
  CUSTOMER: 'customer',
  LIVECONTACT_SUPPORT: 'livecontact-support',
} satisfies UserCompanyRoleType

export const ROLE_CONFIG: ConfigMap<
  UserCompanyRoleTypes | 'default',
  {
    label: string
    badgeClass: string
    avatarClass: string
    /**
     * List of roles that are allowed to assign this role to someone else.
     * Example: `canAssign: [OWNER, ADMIN]` means only Owners and Admins
     * can assign the current role.
     */
    canAssign: UserCompanyRoleTypes[]
    description?: string
    permissions?: RolePermissionsStrict
  }
> = {
  [USER_COMPANY_ROLES.OWNER]: {
    label: 'Owner',
    badgeClass: 'bg-gradient-to-r from-yellow-400 to-orange-500',
    avatarClass:
      'bg-gradient-to-br from-yellow-400 to-orange-500 dark:from-yellow-500 dark:to-orange-600',
    canAssign: [],
    description:
      'Has full access to the company workspace with all administrative privileges. Can create and manage contact cards, manage team members, invite new users, and has access to all workspace settings. The owner has complete control over the system. The ownership can not be transferred.',
    permissions: {
      canAttachManagerVCard: true,
      canCreateVCard: true,
      canDeleteVCard: true,
      vCardBulkActions: true,
      canInvite: true,
      canManageSubscription: true,
      canViewSubscriptionInfo: true,
    },
  },
  [USER_COMPANY_ROLES.ADMIN]: {
    label: 'Admin',
    badgeClass: 'bg-gradient-to-r from-pink-400 to-purple-500',
    avatarClass:
      'bg-gradient-to-br from-pink-400 to-purple-600 dark:from-pink-600 dark:to-purple-700',
    canAssign: [USER_COMPANY_ROLES.OWNER, USER_COMPANY_ROLES.LIVECONTACT_SUPPORT],
    description:
      'Can manage most aspects of the system including creating and managing contact cards, inviting new users, and managing workspace settings. Admins can be assigned by owners and support staff, and they have access to bulk actions for contacts.',
    permissions: {
      canAttachManagerVCard: true,
      canCreateVCard: true,
      canDeleteVCard: true,
      vCardBulkActions: true,
      canInvite: true,
      canManageSubscription: false,
      canViewSubscriptionInfo: false,
    },
  },
  [USER_COMPANY_ROLES.MANAGER]: {
    label: 'Manager',
    badgeClass: 'bg-gradient-to-r from-blue-400 to-blue-600',
    avatarClass: 'bg-gradient-to-br from-blue-400 to-blue-600 dark:from-blue-600 dark:to-blue-700',
    canAssign: [
      USER_COMPANY_ROLES.OWNER,
      USER_COMPANY_ROLES.LIVECONTACT_SUPPORT,
      USER_COMPANY_ROLES.ADMIN,
    ],
    description:
      'Has limited permissions to view and work with contacts but cannot create new contact cards or perform bulk actions. Managers cannot invite new users and have restricted administrative access. They can be assigned by owners, admins, and support staff.',
    permissions: {
      canAttachManagerVCard: false,
      canCreateVCard: false,
      canDeleteVCard: false,
      vCardBulkActions: false,
      canInvite: false,
      canManageSubscription: false,
      canViewSubscriptionInfo: false,
    },
  },
  [USER_COMPANY_ROLES.LIVECONTACT_SUPPORT]: {
    label: 'Support',
    badgeClass: '',
    avatarClass: 'bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-800',
    canAssign: [],
    permissions: {
      canAttachManagerVCard: true,
      canCreateVCard: true,
      canDeleteVCard: true,
      vCardBulkActions: true,
      canInvite: true,
      canManageSubscription: true,
      canViewSubscriptionInfo: false,
    },
  },
  [USER_COMPANY_ROLES.CUSTOMER]: {
    label: 'Customer',
    badgeClass: '',
    avatarClass: 'bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-800',
    canAssign: [],
    permissions: {
      canAttachManagerVCard: false,
      canCreateVCard: false,
      canDeleteVCard: false,
      vCardBulkActions: false,
      canInvite: false,
      canManageSubscription: false,
      canViewSubscriptionInfo: false,
    },
  },
  default: {
    label: '',
    badgeClass: '',
    avatarClass: 'bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-800',
    canAssign: [],
    permissions: {
      canAttachManagerVCard: false,
      canCreateVCard: false,
      canDeleteVCard: false,
      vCardBulkActions: false,
      canInvite: false,
      canManageSubscription: false,
      canViewSubscriptionInfo: false,
    },
  },
}

export type CompanyUserInviteStatusesTypes = MapperType<UserCompanyStatusTypes>
export const COMPANY_USER_INVITE_STATUSES = {
  PENDING: 'pending',
  CONFIRMED: 'confirmed',
} satisfies CompanyUserInviteStatusesTypes
export const COMPANY_USER_INVITE_STATUS_CONFIG = {
  [COMPANY_USER_INVITE_STATUSES.PENDING]: {
    label: 'Pending',
    badgeColor: 'bg-amber-500',
  },
  [COMPANY_USER_INVITE_STATUSES.CONFIRMED]: {
    label: 'Confirmed',
    badgeColor: 'bg-emerald-500',
  },
}
export const COMPANY_USER_INVITE_ROLES = [USER_COMPANY_ROLES.ADMIN, USER_COMPANY_ROLES.MANAGER].map(
  (roleKey) => ({
    title: ROLE_CONFIG[roleKey].label,
    value: roleKey,
    canAssign: ROLE_CONFIG[roleKey].canAssign,
  })
)
